'use client';

import { SpotMarketConfig } from '@velocity-exchange/sdk';
import { UIMarket } from '@velocity-exchange/common';
import { useCallback } from 'react';
import { getCleanedMarketSymbol } from 'src/utils/market';
import { useAllWalletBalances } from 'src/hooks/query/walletBalances/useWalletBalance';

/**
 * Hook that returns a function to get the biggest asset market for a given pool ID.
 */
export function useGetBiggestAssetMarket() {
	const balances = useAllWalletBalances();

	const getBiggestAssetMarket = useCallback(
		(poolId: number): SpotMarketConfig => {
			const spotMarketsLookupByPoolID = UIMarket.spotMarkets.reduce(
				(acc, market) => {
					acc[market.poolId] = [...(acc[market.poolId] || []), market];
					return acc;
				},
				{} as Record<number, SpotMarketConfig[]>
			);
			const relevantSpotMarketsForPoolId = spotMarketsLookupByPoolID[poolId];

			try {
				if (
					!relevantSpotMarketsForPoolId ||
					relevantSpotMarketsForPoolId.length === 0
				) {
					throw new Error(`No spot markets found for pool ${poolId}`);
				}
				const relevantSymbolsForBalancesWithPoolId = Array.from(
					balances.keys()
				).filter((symbol) =>
					relevantSpotMarketsForPoolId.some(
						(market) => getCleanedMarketSymbol(market.symbol) === symbol
					)
				);

				if (relevantSymbolsForBalancesWithPoolId.length === 0) {
					return relevantSpotMarketsForPoolId[0];
				}

				let currentBiggestAssetSymbol = relevantSymbolsForBalancesWithPoolId[0];

				relevantSymbolsForBalancesWithPoolId.forEach((symbol) => {
					const currentBalance = balances.get(symbol);
					const biggestBalance = balances.get(currentBiggestAssetSymbol);
					if (
						currentBalance &&
						biggestBalance &&
						currentBalance.quote.gt(biggestBalance.quote)
					) {
						currentBiggestAssetSymbol = symbol;
					}
				});

				const spotMarketConfig = relevantSpotMarketsForPoolId.find(
					(market) =>
						getCleanedMarketSymbol(market.symbol) === currentBiggestAssetSymbol
				);

				if (!spotMarketConfig) {
					throw new Error('No relevant asset found');
				}

				return spotMarketConfig;
			} catch (e) {
				console.error(e);
				return relevantSpotMarketsForPoolId[0];
			}
		},
		[balances]
	);

	return getBiggestAssetMarket;
}
